import React, { useContext, useEffect, useMemo, useState } from 'react';
import Box from 'Components/Atoms/Box';
import Typography from 'Components/Atoms/Typography';
import IconButton from 'Components/Atoms/IconButton';
import { Event } from 'types/shared/documents/restaurants';
import { ReservationShift } from 'types/shifts';
import { Delete, Edit } from 'Components/Atoms/Icons';
import { LanguageContext } from 'Contexts/LanguageContext';
import useToast from 'CustomHooks/useToast';
import { Severity } from 'Contexts/RestaurantContext';
import { Trans } from 'react-i18next';

import './styles.scss';
import useAlert from 'CustomHooks/useAlert';
import Button from 'Components/Atoms/Button';
import { toCurrencyString } from 'utils/helper';
import { ActiveShift } from 'CustomHooks/useCalendarDates';
import { CalendarContext } from 'Contexts/CalendarContext';
import Select from 'Components/Atoms/Select';
import server from 'utils/server';
import useRestaurant from 'CustomHooks/useRestaurant';
import { cloneEventWithNewId } from 'utils/normalizeEventId';
import { cloneDeep } from 'utils/data-utils';
import isEqual from 'lodash/isEqual';
import { EventTemplate } from 'types/eventTemplates';
import {
  eventFromTemplate,
  getEventTemplateSaveCase,
} from 'utils/eventTemplates';
import EventSettingsDialog, { createDefaultEvent } from './EventSettingsDialog';
import { applyDayOverrideChanges, changeShiftSettings, effectiveShiftSettings } from 'utils/dayOverrides';
import DayCapacityDetails from 'Components/Organisms/DayCapacityDetails';

export type EventSettingsProps = {
  activeShift: ActiveShift<ReservationShift>;
  designV02?: boolean;
  setactiveShift: React.Dispatch<
    React.SetStateAction<ActiveShift<ReservationShift> | null>
  >;
  eventOnly?: boolean;
  handleSubmit?: (
    activeShiftOverride?: ActiveShift<ReservationShift>
  ) => Promise<void>;
};

export function EventDescription(event?: Event | null, language = 'de') {
  const arr: string[] = [];

  if (!!event) {
    // @ts-ignore
    if (event.edited) {
      arr.push(language === 'de' ? 'Bearbeitet' : 'Edited');
    }

    if (event.payAtRestaurant) {
      arr.push(
        language === 'de' ? 'Bezahlung im Restaurant' : 'Pay at Restaurant'
      );
    }

    if (event.optional) {
      arr.push(language === 'de' ? 'Optionales Ticket' : 'Optional Ticket');
      arr.push(language === 'de' ? 'Optionales Ticket' : 'Optional Ticket');
    }

    if (event.maxGuests === 0) {
      arr.push(language === 'de' ? 'Ticketanzahl unbegrenzt' : 'Unlimited tickets');
    } else if (typeof event.maxGuests === 'number') {
      arr.push(
        language === 'de'
          ? `Max ${event.maxGuests} Tickets pro Schicht`
          : `Max ${event.maxGuests} Tickets per Shift`
      );
    }

    if (event.rules?.minGuests) {
      arr.push(
        language === 'de'
          ? `Gruppen ab ${event.rules.minGuests}${
              event.rules.maxGuests ? ` - ${event.rules.maxGuests}` : ''
            } Personen`
          : `Groups from ${event.rules.minGuests}${
              event.rules.maxGuests ? ` - ${event.rules.maxGuests}` : ''
            } Persons`
      );
    } else if (event.rules?.maxGuests) {
      arr.push(
        language === 'de'
          ? `Gruppen bis ${event.rules.maxGuests} Personen`
          : `Groups up to ${event.rules.maxGuests} Persons`
      );
    }

    if (arr.length <= 1) {
      let options = event.options?.length ?? 0;

      let price = event.amount;
      let optionPrice =
        event?.options?.slice().sort((a, b) => (a?.amount ?? 0) - (b?.amount ?? 0))?.[0]
          ?.amount ?? 0;

      if (optionPrice) {
        arr.push(
          language === 'de'
            ? `ab ${toCurrencyString(price + optionPrice, '€', false, 1)}`
            : `from ${toCurrencyString(price + optionPrice, '€', false, 1)}`
        );
      } else {
        arr.push(
          language === 'de'
            ? `${toCurrencyString(price + optionPrice, '€', false, 1)}`
            : `${toCurrencyString(price + optionPrice, '€', false, 1)}`
        );
      }

      if (options > 1)
        arr.push(
          language === 'de' ? `${options} Optionen` : `${options} Options`
        );

      let addOns = event.optionalAddOns?.length ?? 0;

      if (addOns > 1)
        arr.push(language === 'de' ? `${addOns} Add Ons` : `${addOns} Add Ons`);
    }
  }

  return arr.join(' • ');
}

const OldDesign: React.FC<{
  activeShift: ActiveShift<ReservationShift>;
  onOpen: () => void;
  onDelete: () => void;
}> = ({ activeShift, onOpen, onDelete }) => {
  return (
    <Box flex style={{ flexDirection: 'column', marginTop: 16 }}>
      <Typography
        block
        color="subdued"
        variant="text-4"
        style={{ alignSelf: 'flex-start' }}
        translation="calendar"
      >
        Event / Tickets
      </Typography>
      <Box flex fullSize className="space-between">
        <Typography
          block
          color="textPrimary"
          variant="text-3"
          translation="calendar"
        >
          {!activeShift?.shift.additional?.eventV02?.title && (
            <Trans i18nKey="calendar_No event added">No event added</Trans>
          )}
          {activeShift?.shift.additional?.eventV02?.title?.de || ''}
        </Typography>
        <Box style={{ width: 'auto' }}>
          <IconButton color="subdued" size="x-small" onClick={onOpen}>
            <Edit />
          </IconButton>
          {!!activeShift?.shift?.additional?.eventV02 && (
            <IconButton color="subdued" size="x-small" onClick={onDelete}>
              <Delete />
            </IconButton>
          )}
        </Box>
      </Box>
      {activeShift?.shift.additional?.eventV02?.title &&
        !activeShift?.shift.additional?.eventV02?.active && (
          <Typography
            block
            color="error"
            variant="text-5"
            translation="calendar"
            style={{ justifySelf: 'flex-start', alignSelf: 'flex-start' }}
          >
            Event is deactivated
          </Typography>
        )}
    </Box>
  );
};

const NewDesign: React.FC<{
  activeShift: ActiveShift<ReservationShift>;
  onOpen: () => void;
  onDelete: () => void;
}> = ({ activeShift, onOpen, onDelete }) => {
  const { language } = useContext(LanguageContext);

  const description = useMemo(
    () => EventDescription(activeShift?.shift?.additional?.eventV02, language),
    [language, activeShift]
  );

  return (
    <Box style={{ marginTop: 16 }}>
      {!activeShift?.shift?.additional?.eventV02 && (
        <Button variant="secondary" translation="calendar" onClick={onOpen}>
          Add Event
        </Button>
      )}
      {!!activeShift?.shift?.additional?.eventV02 && (
        <Box
          style={{
            padding: 10,
            borderRadius: 4,
            background: 'var(--color-btn-default)',
          }}
        >
          <Typography
            block
            color="textPrimary"
            variant="h5"
            style={{ marginBottom: 3 }}
          >
            {activeShift?.shift.additional?.eventV02?.title?.de ||
              'Kein Event Name'}
          </Typography>
          <Typography
            component="p"
            color="subdued"
            variant="text-3"
            style={{
              justifySelf: 'flex-start',
              alignSelf: 'flex-start',
              marginBottom: 6,
            }}
          >
            {description}
          </Typography>
          {!activeShift?.shift.additional?.eventV02?.active && (
            <Typography
              component="p"
              color="error"
              variant="text-3"
              translation="calendar"
              style={{
                justifySelf: 'flex-start',
                alignSelf: 'flex-start',
                marginBottom: 6,
              }}
            >
              Event is deactivated
            </Typography>
          )}

          <Box flex style={{ justifyContent: 'flex-start' }}>
            <Button
              onClick={onOpen}
              style={{
                marginRight: 8,
                background: 'var(--color-grey-5)',
                padding: '4px 10px',
                fontSize: '14px',
                lineHeight: '19.6px',
                color: 'var(--color-grey-9)',
              }}
              endIcon={(p) => <Edit {...p} />}
              translation="calendar"
            >
              Edit
            </Button>
            <Button
              onClick={onDelete}
              color="error"
              variant="primary"
              style={{
                marginRight: 8,
                background: 'var(--color-critical',
                padding: '4px 10px',
                fontSize: '14px',
                lineHeight: '19.6px',
                color: '#ffffff',
              }}
              endIcon={(p) => <Delete {...p} />}
              translation="calendar"
            >
              Delete
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

type SaveMode = 'new-template' | 'update-template' | 'detached' | 'linked';

// Compared as Firestore stores them. EventDetails sets payAtRestaurant once
// Mollie is loaded, a missing key and false mean the same.
const storedEvent = (event?: Event | null) => {
  const plain = JSON.parse(JSON.stringify(event ?? null));
  return plain && { ...plain, payAtRestaurant: !!plain.payAtRestaurant };
};
const sameStoredEvent = (a?: Event | null, b?: Event | null) =>
  isEqual(storedEvent(a), storedEvent(b));

const EventSettings: React.FC<EventSettingsProps> = ({
  activeShift,
  setactiveShift,
  designV02,
  eventOnly = false,
  handleSubmit: handleShiftSubmit,
}) => {
  const toast = useToast();
  const alert = useAlert();
  const { restaurantId } = useRestaurant();
  const { eventTemplates } = useContext(CalendarContext);

  const [open, setOpen] = useState(eventOnly);
  const [eventState, setEventState] = useState<null | Event>(null);
  // The stored configuration the edit is measured against: set where the
  // event is loaded from the shift (also after a day override reset) and by
  // the template selection; edits never move it. `null`: a new event.
  const [authoringBaseline, setAuthoringBaseline] = useState<Event | null | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) setAuthoringBaseline(undefined);
  }, [open]);

  useEffect(() => {
    if (!open || !activeShift || activeShift.shift.closed) return;

    const source = effectiveShiftSettings(activeShift!.shift, !!activeShift?.allUpcomingShifts).eventV02;
    setEventState(source ?? createDefaultEvent());
    setAuthoringBaseline(source ? cloneDeep(source) : null);
  }, [
    open,
    activeShift?.shift.closed,
    activeShift?.shift.additional?.eventV02,
    activeShift?.shift.overwrite,
    activeShift?.allUpcomingShifts,
  ]);

  const series = !!activeShift?.allUpcomingShifts;
  const dayOnly = activeShift?.type === 'edit' && !series;
  const effectiveEvent = activeShift ? effectiveShiftSettings(activeShift.shift, series).eventV02 : null;
  const displayShift = activeShift ? { ...activeShift, shift: { ...activeShift.shift, additional: effectiveShiftSettings(activeShift.shift, series) } } : activeShift;

  const selectedTemplate = eventTemplates.find(
    (template) => template.id === eventState?.id
  );
  const initialTemplate = eventTemplates.find(
    (template) => template.id === effectiveEvent?.id
  );

  const persistEvent = async (mode: SaveMode, event: Event) => {
    if (!activeShift || !restaurantId || saving) return;

    setSaving(true);

    try {
      let eventToSave = event;

      if (mode === 'new-template') {
        const response = await server.post<{ event: EventTemplate }>(
          `/v03/eventsV02/${restaurantId}`,
          {
            event,
            templateName:
              event.title?.de?.trim() || event.title?.en?.trim() || undefined,
          }
        );
        eventToSave = eventFromTemplate(response.data.event);
      } else if (mode === 'update-template') {
        if (!selectedTemplate) return;
        const response = await server.patch<{ event: EventTemplate }>(
          `/v03/eventsV02/${restaurantId}/event/${selectedTemplate.id}`,
          { event }
        );
        eventToSave = eventFromTemplate(response.data.event);
      } else if (mode === 'detached' && selectedTemplate) {
        eventToSave = cloneEventWithNewId(event);
      }

      // An unchanged save, e.g. right after a day override reset, must not
      // store the series event again as a day override.
      const unchanged = sameStoredEvent(eventToSave, effectiveEvent);
      const markAsEdited =
        mode !== 'update-template' &&
        (!activeShift.allUpcomingShifts || !activeShift.shift.regular);
      const nextActiveShift: ActiveShift<ReservationShift> = unchanged ? activeShift : {
        ...activeShift,
        shift: changeShiftSettings(activeShift.shift, {
          eventV02: { ...eventToSave, ...(markAsEdited ? { edited: true } : {}) },
        }, dayOnly),
      };

      setactiveShift(nextActiveShift);

      if (eventOnly && handleShiftSubmit) {
        await handleShiftSubmit(nextActiveShift);
      }

      setOpen(false);
    } catch (error: any) {
      toast(
        error?.response?.data?.message ||
          error?.message ||
          'Die Event-Einstellungen konnten nicht gespeichert werden.',
        Severity.ERROR
      );
    } finally {
      setSaving(false);
    }
  };

  const requestSaveMode = (event: Event) => {
    const saveCase = getEventTemplateSaveCase(
      event,
      selectedTemplate,
      initialTemplate?.id
    );

    // After a day override reset the series event keeps the template id of
    // the override, so it can differ from that template without any edit.
    if (
      saveCase === 'linked-unchanged' ||
      (saveCase === 'linked-changed' && sameStoredEvent(event, effectiveEvent))
    ) {
      void persistEvent('linked', event);
      return;
    }

    if (saveCase === 'linked-changed' && selectedTemplate) {
      const templateName =
        selectedTemplate.templateName ??
        selectedTemplate.title?.de ??
        selectedTemplate.title?.en ??
        'Unbenannte Vorlage';

      alert({
        title: 'Änderungen an Event-Vorlage',
        description: `Du hast Einstellungen der Vorlage „${templateName}“ geändert. Sollen die Änderungen nur für diese Schicht gelten, über die Vorlage für alle verknüpften zukünftigen Schichten übernommen oder als neue Vorlage gespeichert werden?`,
        maxWidth: 'md',
        actions: [
          {
            id: 'detached',
            label: 'Nur für diese Schicht',
            variant: 'primary',
            onClick: () => void persistEvent('detached', event),
          },
          {
            id: 'update-template',
            label: 'Vorlage aktualisieren',
            onClick: () => void persistEvent('update-template', event),
          },
          {
            id: 'new-template',
            label: 'Als neue Vorlage',
            onClick: () => void persistEvent('new-template', event),
          },
        ],
      });
      return;
    }

    const detached = saveCase === 'detached';
    const newShift =
      activeShift?.type === 'new' || activeShift?.type === 'duplication';

    alert({
      title: detached ? 'Event von Vorlage trennen' : 'Event speichern',
      description: detached
        ? 'Du hast „Ohne Vorlage fortfahren“ gewählt. Möchtest du die Verknüpfung lösen und die Einstellungen nur für diese Schicht speichern oder daraus eine neue Vorlage erstellen?'
        : `Möchtest du das Event ohne Vorlage nur für ${
            newShift ? 'die neue' : 'diese'
          } Schicht speichern oder als neue Vorlage anlegen und direkt verknüpfen?`,
      maxWidth: 'md',
      actions: [
        {
          id: 'detached',
          label: detached ? 'Vorlage trennen' : 'Nur für diese Schicht',
          variant: 'primary',
          onClick: () => void persistEvent('detached', event),
        },
        {
          id: 'new-template',
          label: 'Als neue Vorlage',
          onClick: () => void persistEvent('new-template', event),
        },
      ],
    });
  };

  const removeEvent = () => {
    alert({
      title: 'Delete Event',
      description: 'Are you sure you want to delete this event?',
      onSubmit: () => {
        setactiveShift(current => current ? {
          ...current, shift: changeShiftSettings(current.shift, { eventV02: null }, dayOnly),
        } : current);
      },
    });
  };

  if (!activeShift) return null;

  return (
    <>
      <DayCapacityDetails shift={activeShift.shift} fields={['eventV02']} series={series} onReset={dayOnly ? field => {
        setactiveShift(current => current ? { ...current, shift: applyDayOverrideChanges(current.shift, { set: {}, reset: [field] }) } : current);
      } : undefined} />
      {!designV02 ? (
        <OldDesign
          activeShift={displayShift}
          onOpen={() => setOpen(true)}
          onDelete={removeEvent}
        />
      ) : (
        <NewDesign
          activeShift={displayShift}
          onOpen={() => setOpen(true)}
          onDelete={removeEvent}
        />
      )}

      <EventSettingsDialog
        open={open}
        onClose={() => {
          if (eventOnly) {
            setactiveShift(null);
          }
          setOpen(false);
        }}
        eventState={eventState}
        setEventState={setEventState}
        onSubmit={requestSaveMode}
        authoringBaseline={authoringBaseline}
        header={
          <Box padding="md" className="event-template-selection">
            <DayCapacityDetails shift={activeShift.shift} fields={['eventV02']} series={series} onReset={dayOnly ? field => {
              setactiveShift(current => current ? { ...current, shift: applyDayOverrideChanges(current.shift, { set: {}, reset: [field] }) } : current);
            } : undefined} />
            <Typography block variant="h5">
              Event-Vorlage
            </Typography>
            <Typography block variant="text-3" color="subdued">
              Die Auswahl überschreibt die aktuellen Event-Einstellungen.
            </Typography>
            <Select
              label="Vorlage auswählen"
              margins="12px 0 0"
              value={selectedTemplate?.id ?? ''}
              options={[
                { id: '', label: 'Ohne Vorlage fortfahren' },
                ...eventTemplates.map((template) => ({
                  id: template.id,
                  label:
                    template.templateName ??
                    template.title?.de ??
                    template.title?.en ??
                    template.id,
                })),
              ]}
              onChange={(event) => {
                const template = eventTemplates.find(
                  (candidate) => candidate.id === event.target.value
                );

                if (template) {
                  setEventState(eventFromTemplate(template));
                  setAuthoringBaseline(eventFromTemplate(template));
                } else if (selectedTemplate && eventState) {
                  setEventState(cloneEventWithNewId(eventState));
                  setAuthoringBaseline(null);
                } else {
                  if (!eventState) setAuthoringBaseline(null);
                  setEventState((current) => current ?? createDefaultEvent());
                }
              }}
            />
          </Box>
        }
      />
    </>
  );
};

export default EventSettings;
